/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import * as z from 'zod'
import type { Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { FileJson, RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { MODEL_SHOWCASE } from '@/features/home/constants'
import { FormDirtyIndicator } from '../components/form-dirty-indicator'
import { FormNavigationGuard } from '../components/form-navigation-guard'
import { SettingsSection } from '../components/settings-section'
import { useSettingsForm } from '../hooks/use-settings-form'
import { useUpdateOption } from '../hooks/use-update-option'

/**
 * Validate that a string is either empty or parses into a JSON object.
 * Used to block saving an invalid Home Model Showcase configuration.
 */
function isValidShowcaseJson(value: string | undefined): boolean {
  const trimmed = (value ?? '').trim()
  if (trimmed === '') return true
  try {
    const parsed = JSON.parse(trimmed)
    return (
      typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
    )
  } catch {
    return false
  }
}

const _systemInfoSchema = z.object({
  theme: z.object({
    frontend: z.enum(['default', 'classic', 'noya']),
  }),
  DefaultLanguage: z.string().optional(),
  SystemName: z.string().min(1),
  ServerAddress: z.string().optional(),
  Logo: z.string().url().optional().or(z.literal('')),
  Footer: z.string().optional(),
  About: z.string().optional(),
  SeoTitle: z.string().optional(),
  SeoDescription: z.string().optional(),
  ContactWeChat: z.string().optional(),
  ContactDiscord: z.string().optional(),
  ContactLine: z.string().optional(),
  ContactFacebook: z.string().optional(),
  ContactInstagram: z.string().optional(),
  ContactTiktok: z.string().optional(),
  ContactX: z.string().optional(),
  ContactLinkedIn: z.string().optional(),
  ContactYouTube: z.string().optional(),
  HomePageContent: z.string().optional(),
  HomeModelShowcase: z.string().optional().refine(isValidShowcaseJson),
  legal: z.object({
    user_agreement: z.string().optional(),
    privacy_policy: z.string().optional(),
  }),
})

type SystemInfoFormValues = z.infer<typeof _systemInfoSchema>

type SystemInfoSectionProps = {
  defaultValues: SystemInfoFormValues
}

/**
 * 社交联系方式字段(后台"系统信息"里以两列网格展示)。label 为 i18n key,
 * 顺序与前台页脚 / 关于页的社交图标一致。
 */
const SOCIAL_FIELDS = [
  { name: 'ContactWeChat', label: 'WeChat URL' },
  { name: 'ContactDiscord', label: 'Discord URL' },
  { name: 'ContactLine', label: 'LINE URL' },
  { name: 'ContactFacebook', label: 'Facebook URL' },
  { name: 'ContactInstagram', label: 'Instagram URL' },
  { name: 'ContactTiktok', label: 'TikTok URL' },
  { name: 'ContactX', label: 'X (Twitter) URL' },
  { name: 'ContactLinkedIn', label: 'LinkedIn URL' },
  { name: 'ContactYouTube', label: 'YouTube URL' },
] as const satisfies ReadonlyArray<{
  name: keyof SystemInfoFormValues
  label: string
}>

function normalizeValue(value: unknown): string {
  if (value === undefined || value === null) return ''
  return typeof value === 'string' ? value : String(value)
}

export function SystemInfoSection({ defaultValues }: SystemInfoSectionProps) {
  const { t } = useTranslation()
  const updateOption = useUpdateOption()

  const normalizedDefaults: SystemInfoFormValues = {
    theme: {
      frontend:
        defaultValues.theme?.frontend === 'classic' ||
        defaultValues.theme?.frontend === 'noya'
          ? defaultValues.theme.frontend
          : 'default',
    },
    DefaultLanguage: normalizeValue(defaultValues.DefaultLanguage),
    SystemName: normalizeValue(defaultValues.SystemName),
    ServerAddress: normalizeValue(defaultValues.ServerAddress),
    Logo: normalizeValue(defaultValues.Logo),
    Footer: normalizeValue(defaultValues.Footer),
    About: normalizeValue(defaultValues.About),
    SeoTitle: normalizeValue(defaultValues.SeoTitle),
    SeoDescription: normalizeValue(defaultValues.SeoDescription),
    ContactWeChat: normalizeValue(defaultValues.ContactWeChat),
    ContactDiscord: normalizeValue(defaultValues.ContactDiscord),
    ContactLine: normalizeValue(defaultValues.ContactLine),
    ContactFacebook: normalizeValue(defaultValues.ContactFacebook),
    ContactInstagram: normalizeValue(defaultValues.ContactInstagram),
    ContactTiktok: normalizeValue(defaultValues.ContactTiktok),
    ContactX: normalizeValue(defaultValues.ContactX),
    ContactLinkedIn: normalizeValue(defaultValues.ContactLinkedIn),
    ContactYouTube: normalizeValue(defaultValues.ContactYouTube),
    HomePageContent: normalizeValue(defaultValues.HomePageContent),
    HomeModelShowcase: normalizeValue(defaultValues.HomeModelShowcase),
    legal: {
      user_agreement: normalizeValue(defaultValues.legal?.user_agreement),
      privacy_policy: normalizeValue(defaultValues.legal?.privacy_policy),
    },
  }

  const systemInfoSchemaWithI18n = z.object({
    theme: z.object({
      frontend: z.enum(['default', 'classic', 'noya']),
    }),
    DefaultLanguage: z.string().optional(),
    SystemName: z.string().min(1, {
      error: () => t('System name is required'),
    }),
    ServerAddress: z.string().optional(),
    Logo: z.string().url().optional().or(z.literal('')),
    Footer: z.string().optional(),
    About: z.string().optional(),
    SeoTitle: z.string().optional(),
    SeoDescription: z.string().optional(),
    ContactWeChat: z.string().optional(),
    ContactDiscord: z.string().optional(),
    ContactLine: z.string().optional(),
    ContactFacebook: z.string().optional(),
    ContactInstagram: z.string().optional(),
    ContactTiktok: z.string().optional(),
    ContactX: z.string().optional(),
    ContactLinkedIn: z.string().optional(),
    ContactYouTube: z.string().optional(),
    HomePageContent: z.string().optional(),
    HomeModelShowcase: z
      .string()
      .optional()
      .refine(isValidShowcaseJson, {
        error: () => t('Invalid JSON format'),
      }),
    legal: z.object({
      user_agreement: z.string().optional(),
      privacy_policy: z.string().optional(),
    }),
  })

  const { form, handleSubmit, handleReset, isDirty, isSubmitting } =
    useSettingsForm<SystemInfoFormValues>({
      resolver: zodResolver(systemInfoSchemaWithI18n) as Resolver<
        SystemInfoFormValues,
        unknown,
        SystemInfoFormValues
      >,
      defaultValues: normalizedDefaults,
      onSubmit: async (_data, changedFields) => {
        for (const [key, value] of Object.entries(changedFields)) {
          let v = normalizeValue(value)
          if (key === 'ServerAddress') {
            v = v.replace(/\/+$/, '')
          }
          await updateOption.mutateAsync({
            key,
            value: v,
          })
        }
      },
      onInvalid: () => {
        if (form.formState.errors.HomeModelShowcase) {
          toast.error(t('Invalid JSON format'))
        }
      },
    })

  return (
    <>
      <FormNavigationGuard when={isDirty} />

      <SettingsSection
        title={t('System Information')}
        description={t('Configure basic system information and branding')}
      >
        <Form {...form}>
          <form onSubmit={handleSubmit} className='space-y-6'>
            <FormDirtyIndicator isDirty={isDirty} />
            <FormField
              control={form.control}
              name='theme.frontend'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Frontend Theme')}</FormLabel>
                  <Select
                    items={[
                      { value: 'default', label: t('Default (New Frontend)') },
                      {
                        value: 'classic',
                        label: t('Classic (Legacy Frontend)'),
                      },
                      { value: 'noya', label: t('Noya (New Skin)') },
                    ]}
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className='w-full'>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent alignItemWithTrigger={false}>
                      <SelectGroup>
                        <SelectItem value='default'>
                          {t('Default (New Frontend)')}
                        </SelectItem>
                        <SelectItem value='classic'>
                          {t('Classic (Legacy Frontend)')}
                        </SelectItem>
                        <SelectItem value='noya'>
                          {t('Noya (New Skin)')}
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {t(
                      'Switch between the new frontend and the classic frontend. Changes take effect after page reload.'
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='DefaultLanguage'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Default language')}</FormLabel>
                  <Select
                    items={[
                      { value: 'en', label: t('English') },
                      { value: 'zh-CN', label: t('Simplified Chinese') },
                      { value: 'zh-TW', label: t('Traditional Chinese') },
                      { value: 'ja', label: t('Japanese') },
                      { value: 'ko', label: t('Korean') },
                      { value: 'fr', label: t('French') },
                      { value: 'ru', label: t('Russian') },
                      { value: 'vi', label: t('Vietnamese') },
                    ]}
                    value={field.value || 'en'}
                    onValueChange={(value) => field.onChange(value)}
                  >
                    <FormControl>
                      <SelectTrigger className='w-full'>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent alignItemWithTrigger={false}>
                      <SelectItem value='en'>{t('English')}</SelectItem>
                      <SelectItem value='zh-CN'>
                        {t('Simplified Chinese')}
                      </SelectItem>
                      <SelectItem value='zh-TW'>
                        {t('Traditional Chinese')}
                      </SelectItem>
                      <SelectItem value='ja'>{t('Japanese')}</SelectItem>
                      <SelectItem value='ko'>{t('Korean')}</SelectItem>
                      <SelectItem value='fr'>{t('French')}</SelectItem>
                      <SelectItem value='ru'>{t('Russian')}</SelectItem>
                      <SelectItem value='vi'>{t('Vietnamese')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {t(
                      'Fallback language for users without a language preference'
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='SystemName'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('System Name')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('New API')} {...field} />
                  </FormControl>
                  <FormDescription>
                    {t('The name displayed across the application')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='ServerAddress'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Server Address')}</FormLabel>
                  <FormControl>
                    <Input placeholder='https://yourdomain.com' {...field} />
                  </FormControl>
                  <FormDescription>
                    {t(
                      'The public URL of your server, used for OAuth callbacks, webhooks, and other external integrations'
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='Logo'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Logo URL')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('https://example.com/logo.png')}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t('URL to your logo image (optional)')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='Footer'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Footer')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t(
                        '© 2025 Your Company. All rights reserved.'
                      )}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t('Footer text displayed at the bottom of pages')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='About'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('About')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t(
                        'Enter HTML code (e.g., <p>About us...</p>) or a URL (e.g., https://example.com) to embed as iframe'
                      )}
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t(
                      'Supports HTML markup or iframe embedding. Enter HTML code directly, or provide a complete URL to automatically embed it as an iframe.'
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='SeoTitle'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('SEO Title')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('New API')} {...field} />
                  </FormControl>
                  <FormDescription>
                    {t(
                      'Server-side page title for search engines and social sharing. Leave empty to use the system name.'
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='SeoDescription'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('SEO Description')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t(
                        'Unified AI API gateway and admin dashboard.'
                      )}
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t(
                      'Server-side meta description shown in search results and LLM search snippets.'
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
              {SOCIAL_FIELDS.map((social) => (
                <FormField
                  key={social.name}
                  control={form.control}
                  name={social.name}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t(social.label)}</FormLabel>
                      <FormControl>
                        <Input placeholder='https://...' {...field} />
                      </FormControl>
                      <FormDescription>
                        {t(
                          'Contact link shown on the About / Contact Sales page'
                        )}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>

            <FormField
              control={form.control}
              name='HomePageContent'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Home Page Content')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('Welcome to our New API...')}
                      rows={6}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t(
                      'Content displayed on the home page (supports Markdown)'
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='HomeModelShowcase'
              render={({ field }) => (
                <FormItem>
                  <div className='flex items-center justify-between gap-2'>
                    <FormLabel>{t('Home Model Showcase')}</FormLabel>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() =>
                        field.onChange(JSON.stringify(MODEL_SHOWCASE, null, 2))
                      }
                    >
                      <FileJson className='mr-2 h-4 w-4' />
                      {t('Load default template')}
                    </Button>
                  </div>
                  <FormControl>
                    <Textarea
                      placeholder='{ "providers": [], "moreProviders": [], "headlineZh": "", "headlineEn": "", "textModels": [], "imageModels": [], "videoModels": [] }'
                      rows={12}
                      spellCheck={false}
                      className='font-mono text-xs'
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t(
                      'JSON configuration for the homepage model showcase. Fields: providers (provider cards), moreProviders (extra provider tags), headlineZh / headlineEn (section title), textModels / imageModels / videoModels (model cards). Leave empty to use the built-in default. Use "Load default template" for a valid starting point.'
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='legal.user_agreement'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('User Agreement')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t(
                        'Provide Markdown, HTML, or an external URL for the user agreement'
                      )}
                      rows={6}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t(
                      'Leave empty to disable the agreement requirement. Supports Markdown, HTML, or a full URL to redirect users.'
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='legal.privacy_policy'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Privacy Policy')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t(
                        'Provide Markdown, HTML, or an external URL for the privacy policy'
                      )}
                      rows={6}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t(
                      'Leave empty to disable the privacy policy requirement. Supports Markdown, HTML, or a full URL to redirect users.'
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='flex gap-2'>
              <Button
                type='submit'
                disabled={isSubmitting || updateOption.isPending}
              >
                {updateOption.isPending ? t('Saving...') : t('Save Changes')}
              </Button>
              <Button
                type='button'
                variant='outline'
                onClick={handleReset}
                disabled={!isDirty || updateOption.isPending || isSubmitting}
              >
                <RotateCcw className='mr-2 h-4 w-4' />
                {t('Reset')}
              </Button>
            </div>
          </form>
        </Form>
      </SettingsSection>
    </>
  )
}
